// © 2026 AttainIP Engineering
/**
 * Identity API Client
 * Typed functions for all Identity API endpoints per RFC-0121
 */

import { API_CONFIG } from '../config/api';
import { appConfig } from '../config/appConfig';
import { ApiRequestError } from './client';

const IDENTITY_BASE_URL = API_CONFIG.identityBaseUrl;

interface ApiError {
  code: string;
  message: string;
  correlationId?: string;
}

/**
 * Response from GET /v1/auth/login-options (RFC-0121 A4).
 * Tells the login screen which sign-in affordances to render at runtime.
 */
export interface LoginOptionsResponse {
  magic_link: boolean;
  sso: boolean;
}

/**
 * Response from GET /v1/auth/login
 */
export interface LoginResponse {
  authorization_url: string;
  state: string;
  code_verifier: string;
  token_endpoint: string;
  client_id: string;
  redirect_uri: string;
}

/**
 * Response from POST /v1/auth/token-from-azure and POST /v1/auth/callback
 */
export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  is_new?: boolean;
}

/**
 * One selectable organisation at the Select-Organisation step (RFC-0510 W05/W28).
 *
 * ⚠️ The identifier is `organization_id`, NOT `id`. Only `organization_id` is
 * guaranteed present — `name` and `org_ext_id` are best-effort labels that
 * Identity fills in when it can resolve the organisation, and are `null`
 * otherwise. See `orgChoiceLabel` for how a choice is rendered without ever
 * showing a bare UUID as if it were a name.
 */
export interface OrganizationChoice {
  organization_id: string;
  org_ext_id?: string | null;
  name?: string | null;
  /**
   * The organisation's type, verbatim from Identity's `OrgType` vocabulary
   * (`personal` | `domain` | `entra_tenant` | `enterprise_domain`) — RFC-0510
   * W29. It exists because a PERSONAL organisation has neither a name nor a
   * readable ext_id, so it is the only thing that can say what the choice is.
   *
   * ⚠️ ADDITIVE, AND ABSENT IS NOT A KIND. An Identity that predates W29 sends
   * nothing here, and a newer one may send a value this build has not heard of.
   * Neither may be read as "personal" nor as "enterprise" — `orgChoiceLabel`
   * falls back to the name/ext_id chain instead of guessing.
   */
  org_type?: string | null;
}

/**
 * A login that stopped at Select-Organisation (RFC-0510 W05/W15).
 *
 * Returned by a login path INSTEAD of a token pair when the authenticated human
 * holds more than one active membership. It carries NO access token and NO
 * refresh token, deliberately: nothing has been granted yet. The human must POST
 * `org_selection_token` plus their chosen `organization_id` to
 * `/v1/auth/org-selection/exchange` to obtain a real, org-scoped session.
 *
 * ⚠️ `org_selection_token` GRANTS NOTHING and is short-lived (`expires_in`,
 * currently 10 minutes). It must never be written where a session token lives
 * (see `useAuthStore` / `getStoredRefreshToken`) and must never be sent as a
 * bearer to anything but the exchange — every ordinary audience gate refuses it,
 * so a session built from it looks exactly like the beta regression this closes:
 * login 200, then `GET /v1/auth/me` 401 `invalid_token`.
 */
export interface OrgSelectionRequiredResponse {
  requires_org_selection: true;
  org_selection_token: string;
  organizations: OrganizationChoice[];
  expires_in: number;
}

/** What a SSO/Azure login can return: a real session, or the picker challenge. */
export type AzureTokenExchangeResult = TokenResponse | OrgSelectionRequiredResponse;

/** What a magic-link validation can return: a real session, or the picker challenge. */
export type MagicLinkValidateResult = MagicLinkValidateResponse | OrgSelectionRequiredResponse;

/**
 * Tell the two disjoint login outcomes apart.
 *
 * Identity's own contract: the challenge shape has `requires_org_selection:
 * true` and no `access_token`; the token shape has an `access_token` and no
 * `requires_org_selection`. They are disjoint BY REQUIRED FIELDS, so we
 * discriminate on the POSITIVE presence of the discriminator — never on which
 * fields happen to be missing, which would misread a partial/errored token
 * response as a selection challenge.
 */
export function requiresOrgSelection(
  result: AzureTokenExchangeResult | MagicLinkValidateResult | null | undefined,
): result is OrgSelectionRequiredResponse {
  return (result as OrgSelectionRequiredResponse | null | undefined)
    ?.requires_org_selection === true;
}

/**
 * Why an org-selection exchange was refused. The two named reasons get their own
 * message and their own recovery, because they mean different things:
 *
 *  - `membership_revoked` (HTTP 403 `membership_required`) — Identity re-read
 *    the human's ACTIVE memberships from the repository and they hold none in
 *    the organisation they picked. A revocation between mint and exchange is
 *    caught here deliberately; it is the security property of the feature. The
 *    human keeps their other choices, so this returns them to the picker.
 *  - `selection_expired` (HTTP 400 `invalid_org_selection_token`) — the
 *    intermediate token is no longer honoured. Identity does not distinguish
 *    "expired" from "malformed" (one message covers both) and, since the only
 *    token the picker ever holds is one Identity just minted, the realistic
 *    cause is the 10-minute TTL running out. Either way the remedy is the same
 *    and there is nothing left to pick with: sign in again. Never retried
 *    silently.
 */
export type OrgSelectionFailureReason =
  | 'membership_revoked'
  | 'selection_expired'
  | 'unknown';

/** Discriminated outcome of an org-selection exchange — mirrors `RestoreResult`. */
export type OrgSelectionExchangeResult =
  | { ok: true; tokens: TokenResponse }
  | { ok: false; reason: OrgSelectionFailureReason; message: string };

/**
 * Read `{code, message}` out of whichever error envelope came back.
 *
 * Identity raises FastAPI `HTTPException(detail=ErrorDetail(...))`, which
 * serialises as `{"detail": {"code": ..., "message": ...}}`; some surfaces wrap
 * it as `{"error": {...}}` (the `ErrorResponse` schema) and some return the
 * detail flat. Accept all three rather than guess.
 */
function readErrorDetail(body: unknown): { code?: string; message?: string } {
  if (!body || typeof body !== 'object') return {};
  const record = body as Record<string, unknown>;
  for (const candidate of [record.detail, record.error, record]) {
    if (candidate && typeof candidate === 'object') {
      const { code, message } = candidate as { code?: unknown; message?: unknown };
      if (typeof code === 'string' || typeof message === 'string') {
        return {
          code: typeof code === 'string' ? code : undefined,
          message: typeof message === 'string' ? message : undefined,
        };
      }
    }
  }
  return {};
}

/**
 * Second half of the Select-Organisation step (RFC-0510 W05/W28).
 * POST /v1/auth/org-selection/exchange
 *
 * Trades the grant-nothing intermediate token plus the organisation the human
 * chose for a NORMAL token pair scoped to that organisation. This is the ONLY
 * request `org_selection_token` is ever attached to, and it goes in the body —
 * never as an `Authorization` bearer.
 *
 * Returns a discriminated result instead of throwing, so callers must handle the
 * refusal explicitly: a refused exchange is a normal, expected outcome here, not
 * an exception.
 */
export async function exchangeOrgSelection(
  orgSelectionToken: string,
  organizationId: string,
): Promise<OrgSelectionExchangeResult> {
  let response: Response;
  try {
    response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/org-selection/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        org_selection_token: orgSelectionToken,
        organization_id: organizationId,
      }),
    });
  } catch (err) {
    return {
      ok: false,
      reason: 'unknown',
      message: err instanceof Error ? err.message : 'Could not reach the sign-in service.',
    };
  }

  if (response.ok) {
    return { ok: true, tokens: (await response.json()) as TokenResponse };
  }

  const { code, message } = readErrorDetail(await response.json().catch(() => null));

  // The code is authoritative when Identity sent one; the status is the fallback
  // for anything in front of it (a gateway, a proxy) that answered instead.
  let reason: OrgSelectionFailureReason;
  if (code === 'membership_required') {
    reason = 'membership_revoked';
  } else if (code === 'invalid_org_selection_token') {
    reason = 'selection_expired';
  } else if (response.status === 403) {
    reason = 'membership_revoked';
  } else if (response.status === 400) {
    reason = 'selection_expired';
  } else {
    reason = 'unknown';
  }

  return {
    ok: false,
    reason,
    message: message ?? 'The organisation selection could not be completed.',
  };
}

/**
 * One organisation the "acting in" control may offer (RFC-0510 W39).
 * From `GET /v1/auth/organizations`.
 *
 * The login picker's `OrganizationChoice` plus the two facts that make it a
 * decision a human can actually make while working:
 *  - `membership_kind` — `member` or `guest`. It decides what they may do there
 *    and whose credit pool the work draws on (RFC-0510 D8);
 *  - `is_current` — the organisation this session is scoped to right now.
 *
 * ⚠️ `name` is BEST EFFORT and may be Identity's placeholder for an
 * organisation it could not name. Render through `orgIdentity`, never raw.
 */
export interface SwitchableOrganization {
  organization_id: string;
  org_ext_id?: string | null;
  name?: string | null;
  org_type?: string | null;
  membership_kind: 'member' | 'guest' | string;
  is_current: boolean;
}

/** What `GET /v1/auth/organizations` returns (RFC-0510 W39). */
export interface SwitchableOrganizationsResponse {
  organizations: SwitchableOrganization[];
  current_organization_id: string;
}

/**
 * The organisations this session may act in (RFC-0510 W39).
 * GET /v1/auth/organizations
 *
 * W32's offerable set, RE-READ by Identity at this instant — so a membership
 * revoked since sign-in is already absent, and the control can never show a
 * door the switch would refuse. A single entry means there is nothing to switch
 * between and the caller renders NO control at all.
 *
 * 403 on a non-enterprise session (`org_switch_unavailable`): the switcher is
 * enterprise-only in v1 because the self-serve surface has no
 * active-organisation indicator to switch under.
 */
export async function fetchSwitchableOrganizations(
  accessToken: string,
): Promise<SwitchableOrganizationsResponse> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/organizations`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'ORGANIZATIONS_ERROR',
      message: 'Failed to load organisations',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/** Why a switch was refused. Mirrors `OrgSelectionFailureReason`, because the
 *  switch IS the org-selection exchange taken later and meets the same
 *  refusals:
 *   - `membership_revoked` (403 `membership_required`) — Identity re-read the
 *     human's live memberships and they hold none in that organisation. The
 *     guest whose access was withdrawn while they were signed in lands here;
 *   - `switch_unavailable` (403 `org_switch_unavailable`) — this surface has no
 *     acting-in control. A client that renders one on self-serve is the bug;
 *   - `unknown` — anything else, including the network. */
export type OrgSwitchFailureReason =
  | 'membership_revoked'
  | 'switch_unavailable'
  | 'unknown';

/** Discriminated outcome of a switch — mirrors `OrgSelectionExchangeResult`. */
export type OrgSwitchResult =
  | { ok: true; tokens: TokenResponse }
  | { ok: false; reason: OrgSwitchFailureReason; message: string };

/**
 * Move this session into another organisation (RFC-0510 W39).
 * POST /v1/auth/organizations/switch
 *
 * ⚠️ THIS MINTS A NEW SESSION. The returned token pair is scoped to the chosen
 * organisation and the caller MUST adopt it (see `adoptSession`). A switch that
 * only changed what the interface displays would leave the session scoped to the
 * previous organisation and every later call misstating who is acting.
 *
 * Returns a discriminated result instead of throwing: a refused switch is a
 * normal, expected outcome — it is how a revoked guest finds out.
 */
export async function switchOrganization(
  accessToken: string,
  organizationId: string,
): Promise<OrgSwitchResult> {
  let response: Response;
  try {
    response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/organizations/switch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify({ organization_id: organizationId }),
    });
  } catch (err) {
    return {
      ok: false,
      reason: 'unknown',
      message: err instanceof Error ? err.message : 'Could not reach the sign-in service.',
    };
  }

  if (response.ok) {
    return { ok: true, tokens: (await response.json()) as TokenResponse };
  }

  const { code, message } = readErrorDetail(await response.json().catch(() => null));

  // The code is authoritative when Identity sent one; the status is the fallback
  // for anything in front of it (a gateway, a proxy) that answered instead.
  let reason: OrgSwitchFailureReason;
  if (code === 'membership_required') {
    reason = 'membership_revoked';
  } else if (code === 'org_switch_unavailable') {
    reason = 'switch_unavailable';
  } else if (response.status === 403) {
    reason = 'membership_revoked';
  } else {
    reason = 'unknown';
  }

  return {
    ok: false,
    reason,
    message: message ?? 'You could not be moved into that organisation.',
  };
}

/**
 * Response from POST /v1/auth/magic-link/validate
 */
export interface MagicLinkValidateResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  is_new_user: boolean;
  user: {
    id: string;
    email: string;
    display_name: string;
    organization_id: string;
    /** RFC-0500/RFC-0121 A4 — real account audience(s), enterprise or
     *  self-serve; magic link no longer implies self-serve since an
     *  enterprise firm can opt in. */
    audiences: string[];
  };
}

/**
 * Response from POST /v1/auth/refresh
 */
export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
}

/**
 * Response from GET /v1/auth/me
 */
export interface MeResponse {
  id: string;
  email: string;
  display_name: string;
  organization_id: string;
  roles: string[];
  /** RFC-0500/RFC-0121 A4 — real account audience(s), enterprise or self-serve. */
  audiences: string[];
  /** RFC-0510 W06 — the acting organisation's human-readable identity, so the
   *  active-organisation indicator can render a name (or a readable `org_ext_id`
   *  fallback) and NEVER the bare `organization_id` UUID. Both nullable; when
   *  the org can't be resolved the client fails closed to showing nothing. */
  org_name?: string | null;
  org_ext_id?: string | null;
  /** RFC-0500 A5 — the acting org's product configuration. Tri-state and
   *  unresolved: a missing/null value means "no override, use the
   *  environment default", NOT "off". */
  features?: {
    workspace?: boolean | null;
    /** RFC-0511 B — the firm's per-org SET of enabled workflow keys. Same
     *  tri-state contract: null/absent means "no per-org set, the environment
     *  decides", NOT "none of them" (which is `[]`). */
    workflows?: string[] | null;
  };
}

/**
 * Response from POST /v1/auth/magic-link/request
 */
export interface MagicLinkRequestResponse {
  message: string;
  expires_in: number;
}

/**
 * Get OAuth2 login URL and PKCE parameters
 * GET /v1/auth/login?redirect_uri={redirectUri}
 */
export async function getLoginUrl(redirectUri: string): Promise<LoginResponse> {
  const params = new URLSearchParams({ redirect_uri: redirectUri });

  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/login?${params}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'LOGIN_URL_ERROR',
      message: 'Failed to get login URL',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Exchange authorization code for access token
 * POST /v1/auth/callback
 *
 * ⚠️ Currently unused — the SPA does the PKCE exchange with Azure itself and
 * then calls `exchangeAzureTokens` (see `CallbackPage`). Typed as the same
 * two-shape union anyway (RFC-0510 W15): Identity's `/callback` is
 * membership-aware too, so anything reviving this path is forced by the type to
 * narrow with `requiresOrgSelection` rather than reintroduce the assumption that
 * a login always yields a token pair.
 */
export async function exchangeCode(
  code: string,
  redirectUri: string
): Promise<AzureTokenExchangeResult> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'CODE_EXCHANGE_ERROR',
      message: 'Failed to exchange authorization code',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Exchange Azure AD tokens for Attain access token
 * POST /v1/auth/token-from-azure
 *
 * ⚠️ Returns ONE OF TWO disjoint shapes (RFC-0510 W15): a token pair, or an
 * org-selection challenge when the human holds more than one active membership.
 * Callers MUST narrow with `requiresOrgSelection` before reading `access_token`.
 */
export async function exchangeAzureTokens(
  accessToken: string,
  idToken: string
): Promise<AzureTokenExchangeResult> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/token-from-azure`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      access_token: accessToken,
      id_token: idToken,
      account_type: API_CONFIG.accountType,
    }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'AZURE_TOKEN_EXCHANGE_ERROR',
      message: 'Failed to exchange Azure tokens',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Fetch the runtime login options for an audience (RFC-0121 A4).
 * GET /v1/auth/login-options?aud=...&email=...
 *
 * The login screen uses this to make the magic-link affordance runtime-aware
 * (per-org / per-audience) instead of relying solely on the build-time flavour.
 * `email`, once the user has entered one, lets the enterprise login resolve
 * the firm's per-org magic-link policy — the firm isn't known pre-login.
 */
export async function fetchLoginOptions(aud?: string, email?: string): Promise<LoginOptionsResponse> {
  const params = new URLSearchParams();
  if (aud) params.set('aud', aud);
  if (email) params.set('email', email);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/login-options${qs}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'LOGIN_OPTIONS_ERROR',
      message: 'Failed to fetch login options',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Request a magic link to be sent via email
 * POST /v1/auth/magic-link/request
 */
export async function requestMagicLink(
  email: string,
  name?: string,
  referralCode?: string,
  verifyBaseUrl?: string,
  // The acting surface. Defaults to this build's flavour so the enterprise app
  // always requests aud=enterprise (SSO-only unless the firm opted in) and the
  // self-serve app requests aud=selfserve (open) — RFC-0500 / RFC-0121 A4.
  aud: string = appConfig.accountType
): Promise<MagicLinkRequestResponse> {
  const body: Record<string, string> = { email, aud };
  if (name) body.name = name;
  if (referralCode) body.referral_code = referralCode;
  if (verifyBaseUrl) body.verify_base_url = verifyBaseUrl;

  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/magic-link/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'MAGIC_LINK_REQUEST_ERROR',
      message: 'Failed to request magic link',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Validate a magic link token and get access token
 * POST /v1/auth/magic-link/validate
 *
 * ⚠️ Returns ONE OF TWO disjoint shapes (RFC-0510 W15): the usual token pair +
 * user, or an org-selection challenge when the human holds more than one active
 * membership. Callers MUST narrow with `requiresOrgSelection` first.
 */
export async function validateMagicLink(token: string): Promise<MagicLinkValidateResult> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/magic-link/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'MAGIC_LINK_VALIDATION_ERROR',
      message: 'Failed to validate magic link',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Refresh access token using stored refresh token
 * POST /v1/auth/refresh
 */
export async function refreshToken(token: string): Promise<RefreshResponse> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ refresh_token: token }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'TOKEN_REFRESH_ERROR',
      message: 'Failed to refresh token',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Get current authenticated user information
 * GET /v1/auth/me
 */
export async function getCurrentUser(accessToken: string): Promise<MeResponse> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'GET_USER_ERROR',
      message: 'Failed to get user information',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }

  return response.json();
}

/**
 * Log out and invalidate session
 * POST /v1/auth/logout
 */
export async function logout(): Promise<void> {
  const response = await fetch(`${IDENTITY_BASE_URL}/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      code: 'LOGOUT_ERROR',
      message: 'Failed to log out',
    }));
    throw new ApiRequestError(error.code, error.message, error.correlationId);
  }
}
